import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, Plus, Trash, X, MagnifyingGlass, Truck, Users, ListBullets, Receipt, Eye, FilePdf, FileXls } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

export function PurchasesModule() {
  const [tab, setTab] = useState("nueva-compra");

  return (
    <div className="space-y-5" data-testid="purchases-module">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Truck size={24} weight="duotone" className="text-blue-600" /> Compras</h1>
        <p className="text-sm text-slate-500">Gestion de compras, proveedores e ingreso de inventario</p>
      </div>
      <div className="flex gap-1 flex-wrap">
        {[
          { id: "nueva-compra", label: "Nueva Compra", icon: Plus },
          { id: "historial", label: "Historial", icon: ListBullets },
          { id: "proveedores", label: "Proveedores", icon: Users },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md transition-colors ${tab === t.id ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid={`purchases-tab-${t.id}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "nueva-compra" && <NewPurchaseOrder onCreated={() => setTab("historial")} />}
      {tab === "historial" && <PurchaseHistory />}
      {tab === "proveedores" && <SuppliersManager />}
    </div>
  );
}

// =================== NEW PURCHASE ORDER ===================
function NewPurchaseOrder({ onCreated }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [showSupplierList, setShowSupplierList] = useState(false);
  const [items, setItems] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [showProductList, setShowProductList] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("transferencia");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [taxRate, setTaxRate] = useState(16);
  const [saving, setSaving] = useState(false);
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ sku: "", name: "", description: "", unit_cost: "", sale_price: "" });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/purchases/suppliers`, { headers }),
      axios.get(`${API}/products?limit=5000`, { headers }),
    ]).then(([sRes, pRes]) => {
      setSuppliers(sRes.data || []);
      setProducts(pRes.data?.products || []);
    }).catch(() => {});
  }, [token]);

  const filteredSuppliers = supplierSearch.trim()
    ? suppliers.filter(s => s.name?.toLowerCase().includes(supplierSearch.toLowerCase()) || s.rfc?.toLowerCase().includes(supplierSearch.toLowerCase())).slice(0, 8)
    : suppliers.slice(0, 8);

  const filteredProducts = productSearch.trim()
    ? products.filter(p => p.name?.toLowerCase().includes(productSearch.toLowerCase()) || p.sku?.toLowerCase().includes(productSearch.toLowerCase())).slice(0, 10)
    : [];

  const addExistingProduct = (product) => {
    const existing = items.find(i => i.product_id === product.id);
    if (existing) {
      setItems(items.map(i => i.product_id === product.id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      setItems([...items, {
        id: Date.now().toString(), product_id: product.id, sku: product.sku, name: product.name,
        quantity: 1, unit_cost: 0, is_new_product: false,
      }]);
    }
    setProductSearch("");
    setShowProductList(false);
  };

  const addNewProduct = () => {
    if (!newProduct.sku.trim() || !newProduct.name.trim()) {
      toast.error("SKU y nombre son requeridos");
      return;
    }
    setItems([...items, {
      id: Date.now().toString(), product_id: null, sku: newProduct.sku.trim().toUpperCase(),
      name: newProduct.name.trim(), description: newProduct.description,
      quantity: 1, unit_cost: parseFloat(newProduct.unit_cost) || 0,
      sale_price: parseFloat(newProduct.sale_price) || 0,
      is_new_product: true,
    }]);
    setNewProduct({ sku: "", name: "", description: "", unit_cost: "", sale_price: "" });
    setShowNewProduct(false);
    toast.success("Producto nuevo agregado a la orden");
  };

  const subtotal = items.reduce((s, i) => s + (i.quantity * i.unit_cost), 0);
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal + taxAmount;
  const round = (n) => Math.round(n * 100) / 100;

  const handleSave = async () => {
    if (!selectedSupplier) { toast.error("Selecciona un proveedor"); return; }
    if (items.length === 0) { toast.error("Agrega al menos un producto"); return; }
    if (items.some(i => i.unit_cost <= 0)) { toast.error("Todos los productos deben tener costo"); return; }

    setSaving(true);
    try {
      const res = await axios.post(`${API}/purchases/orders`, {
        supplier_id: selectedSupplier.id,
        items: items.map(i => ({
          product_id: i.product_id, sku: i.sku, name: i.name,
          description: i.description || "", quantity: i.quantity, unit_cost: i.unit_cost,
          sale_price: i.sale_price || 0, is_new_product: i.is_new_product,
        })),
        tax_rate: taxRate, payment_method: paymentMethod, reference, notes,
      }, { headers });
      toast.success(`Compra ${res.data.folio} registrada - inventario actualizado`);
      setItems([]);
      setSelectedSupplier(null);
      setReference("");
      setNotes("");
      onCreated?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="new-purchase-order">
      {/* Supplier selector */}
      <div className="bg-white border rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_200px_200px] gap-4 items-end">
          <div className="relative">
            <label className="text-[10px] font-semibold text-slate-500 uppercase">Proveedor:</label>
            <div className="relative mt-1">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={selectedSupplier ? selectedSupplier.name : supplierSearch}
                onChange={e => { setSupplierSearch(e.target.value); setSelectedSupplier(null); setShowSupplierList(true); }}
                onFocus={() => setShowSupplierList(true)}
                placeholder="Buscar proveedor..."
                className="w-full h-9 pl-8 pr-8 border-2 border-dashed border-blue-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                data-testid="purchase-supplier-search"
              />
              {selectedSupplier && <button onClick={() => { setSelectedSupplier(null); setSupplierSearch(""); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500"><X size={14} /></button>}
            </div>
            {showSupplierList && !selectedSupplier && (
              <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredSuppliers.length === 0 ? (
                  <p className="text-xs text-slate-400 p-3 text-center">Sin proveedores. Crea uno en la pestana Proveedores.</p>
                ) : filteredSuppliers.map(s => (
                  <button key={s.id} onClick={() => { setSelectedSupplier(s); setSupplierSearch(""); setShowSupplierList(false); setPaymentMethod(s.payment_method || "transferencia"); }}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-xs border-b last:border-0">
                    <p className="font-semibold text-slate-800">{s.name}</p>
                    <p className="text-[10px] text-slate-500">{s.rfc} | {s.phone}</p>
                  </button>
                ))}
              </div>
            )}
            {showSupplierList && <div className="fixed inset-0 z-10" onClick={() => setShowSupplierList(false)} />}
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase">Metodo de Pago:</label>
            <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className="w-full h-9 mt-1 px-2 border rounded-lg text-sm" data-testid="purchase-payment-method">
              <option value="transferencia">Transferencia</option>
              <option value="efectivo">Efectivo</option>
              <option value="cheque">Cheque</option>
              <option value="credito">Credito</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase">Referencia / Factura:</label>
            <Input value={reference} onChange={e => setReference(e.target.value)} className="h-9 text-sm mt-1" placeholder="No. factura proveedor" data-testid="purchase-reference" />
          </div>
        </div>
      </div>

      {/* Items table */}
      <div className="bg-white border rounded-xl overflow-visible">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-white">
              <th className="px-4 py-3 text-left text-xs font-semibold text-blue-700 w-28">SKU</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-blue-700">Producto</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-blue-700 w-20">Cant.</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-blue-700 w-28">Costo Unit.</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-blue-700 w-28">Importe</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b hover:bg-slate-50">
                <td className="px-4 py-2 text-xs font-mono text-slate-700">
                  {item.sku}
                  {item.is_new_product && <span className="ml-1 text-[9px] bg-green-100 text-green-700 px-1 rounded">NUEVO</span>}
                </td>
                <td className="px-4 py-2 text-xs font-semibold text-slate-800">{item.name}</td>
                <td className="px-4 py-2">
                  <input type="number" value={item.quantity} min={1}
                    onChange={e => setItems(items.map(i => i.id === item.id ? { ...i, quantity: Math.max(1, parseInt(e.target.value) || 1) } : i))}
                    className="w-16 h-6 text-center text-xs font-mono border rounded" />
                </td>
                <td className="px-4 py-2 text-right">
                  <input type="number" value={item.unit_cost} step="0.01"
                    onChange={e => setItems(items.map(i => i.id === item.id ? { ...i, unit_cost: parseFloat(e.target.value) || 0 } : i))}
                    className="w-24 h-6 text-right text-xs font-mono border rounded px-1" />
                </td>
                <td className="px-4 py-2 text-right font-mono font-bold text-slate-900">${round(item.quantity * item.unit_cost).toLocaleString("es-MX")}</td>
                <td className="px-4 py-2"><button onClick={() => setItems(items.filter(i => i.id !== item.id))} className="text-slate-300 hover:text-red-500"><Trash size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Add product */}
        <div className="p-3 space-y-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={productSearch}
              onChange={e => { setProductSearch(e.target.value); setShowProductList(true); }}
              onFocus={() => productSearch && setShowProductList(true)}
              placeholder="Buscar producto existente por nombre o SKU..."
              className="w-full h-10 pl-9 border-2 border-dashed border-blue-300 rounded-lg text-sm text-center focus:outline-none focus:border-blue-500"
              data-testid="purchase-product-search"
            />
            {showProductList && productSearch.trim() && <div className="fixed inset-0 z-10" onClick={() => setShowProductList(false)} />}
            {showProductList && productSearch.trim() && (
              <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-xl max-h-56 overflow-y-auto">
                {filteredProducts.length === 0 ? (
                  <p className="text-xs text-slate-400 p-3 text-center">Sin resultados. <button onClick={() => { setShowProductList(false); setShowNewProduct(true); setNewProduct(p => ({ ...p, name: productSearch })); setProductSearch(""); }} className="text-blue-600 underline">Crear producto nuevo</button></p>
                ) : filteredProducts.map(p => (
                  <button key={p.id} onClick={() => addExistingProduct(p)}
                    data-testid={`purchase-product-item-${p.id}`}
                    className="w-full text-left px-4 py-2.5 hover:bg-blue-50 text-xs border-b last:border-0 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-800">{p.name}</p>
                      <p className="text-[10px] text-slate-500 font-mono">{p.sku} | Stock: {p.stock_qty || 0}</p>
                    </div>
                    <span className="font-bold text-slate-600 font-mono">${(p.precio_lista || 0).toLocaleString("es-MX")}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button onClick={() => setShowNewProduct(true)} className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1" data-testid="purchase-new-product-btn">
            <Plus size={12} /> Dar de alta producto nuevo
          </button>
        </div>

        {/* New product inline form */}
        {showNewProduct && (
          <div className="mx-3 mb-3 p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold text-green-800 uppercase">Nuevo Producto</h4>
              <button onClick={() => setShowNewProduct(false)} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="text-[10px] font-semibold text-slate-500">SKU *</label>
                <Input value={newProduct.sku} onChange={e => setNewProduct({ ...newProduct, sku: e.target.value })} className="h-8 text-xs mt-0.5" placeholder="PROD-001" data-testid="new-prod-sku" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] font-semibold text-slate-500">Nombre *</label>
                <Input value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} className="h-8 text-xs mt-0.5" placeholder="Nombre del producto" data-testid="new-prod-name" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500">Costo</label>
                <Input type="number" value={newProduct.unit_cost} onChange={e => setNewProduct({ ...newProduct, unit_cost: e.target.value })} className="h-8 text-xs mt-0.5" placeholder="0.00" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-500">Precio Venta</label>
                <Input type="number" value={newProduct.sale_price} onChange={e => setNewProduct({ ...newProduct, sale_price: e.target.value })} className="h-8 text-xs mt-0.5" placeholder="0.00" />
              </div>
            </div>
            <Button size="sm" className="h-8 text-xs bg-green-600 hover:bg-green-700" onClick={addNewProduct} data-testid="new-prod-add">
              <Plus size={12} className="mr-1" /> Agregar a la orden
            </Button>
          </div>
        )}
      </div>

      {/* Bottom: Notes + Totals */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div className="bg-white border rounded-xl p-4">
          <label className="text-[10px] font-semibold text-slate-500 uppercase">Notas:</label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Observaciones de la compra..." className="text-sm min-h-[60px] mt-1" />
        </div>
        <div className="bg-white border rounded-xl p-4 space-y-2">
          <div className="flex justify-between text-sm"><span className="text-slate-600">Subtotal:</span><span className="font-mono font-semibold">${round(subtotal).toLocaleString("es-MX")}</span></div>
          <div className="flex justify-between text-sm items-center">
            <div className="flex items-center gap-1">
              <span className="text-slate-600">I.V.A.</span>
              <select value={taxRate} onChange={e => setTaxRate(parseFloat(e.target.value))} className="h-6 text-xs border rounded px-1">
                <option value={16}>16%</option><option value={0}>0%</option><option value={8}>8%</option>
              </select>
            </div>
            <span className="font-mono font-semibold">${round(taxAmount).toLocaleString("es-MX")}</span>
          </div>
          <div className="border-t pt-2 flex justify-between">
            <span className="text-lg font-bold text-slate-900">Total:</span>
            <span className="text-2xl font-extrabold text-slate-900 font-mono" data-testid="purchase-total">${round(total).toLocaleString("es-MX")}</span>
          </div>
          <Button className="w-full h-10 text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white mt-2" onClick={handleSave} disabled={saving || items.length === 0} data-testid="purchase-save-btn">
            {saving ? "Guardando..." : "Registrar Compra"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// =================== PURCHASE HISTORY ===================
function PurchaseHistory() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === "admin";
  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterSupplier, setFilterSupplier] = useState("");
  const [exporting, setExporting] = useState(false);
  const PAGE_SIZE = 50;

  useEffect(() => {
    axios.get(`${API}/purchases/suppliers`, { headers }).then(r => setSuppliers(r.data || [])).catch(() => {});
  }, [token]);

  const fetchOrders = useCallback(async (pg = 0) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/purchases/orders?limit=${PAGE_SIZE}&skip=${pg * PAGE_SIZE}`, { headers });
      setOrders(r.data.orders || []);
      setTotalOrders(r.data.total || 0);
    } catch { toast.error("Error al cargar historial"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchOrders(page); }, [fetchOrders, page]);

  const totalPages = Math.max(1, Math.ceil(totalOrders / PAGE_SIZE));

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterDateFrom) params.append("date_from", filterDateFrom);
      if (filterDateTo) params.append("date_to", filterDateTo);
      if (filterSupplier) params.append("supplier_id", filterSupplier);
      const res = await axios.get(`${API}/purchases/orders/export/excel?${params.toString()}`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `historial_compras.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch { toast.error("Error al exportar"); }
    finally { setExporting(false); }
  };

  const handleDownloadPdf = async (orderId, folio) => {
    try {
      const res = await axios.get(`${API}/purchases/orders/${orderId}/pdf`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Compra_${folio}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch { toast.error("Error al generar PDF"); }
  };

  const handleDelete = async (orderId) => {
    setDeleting(orderId);
    try {
      await axios.delete(`${API}/purchases/orders/${orderId}`, { headers });
      toast.success("Orden eliminada");
      setOrders(prev => prev.filter(o => o.id !== orderId));
      setTotalOrders(prev => prev - 1);
      if (selectedOrder?.id === orderId) setSelectedOrder(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
    finally { setDeleting(null); }
  };

  if (loading) return <div className="flex justify-center py-10"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-4" data-testid="purchase-history">
      <div className="flex gap-4">
        <div className="bg-white border rounded-lg px-4 py-3"><p className="text-[10px] text-slate-500 uppercase font-semibold">Total Ordenes</p><p className="text-xl font-extrabold text-slate-900">{totalOrders}</p></div>
        <div className="bg-white border rounded-lg px-4 py-3"><p className="text-[10px] text-slate-500 uppercase font-semibold">Monto Total</p><p className="text-xl font-extrabold text-blue-700 font-mono">${orders.reduce((s, o) => s + (o.total || 0), 0).toLocaleString("es-MX")}</p></div>
      </div>

      {/* Filters + Export */}
      <div className="bg-white border rounded-xl p-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[10px] font-semibold text-slate-500 uppercase">Desde</label>
          <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
            className="block w-full h-9 mt-0.5 px-2 border rounded-lg text-sm" data-testid="purchase-filter-from" />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-slate-500 uppercase">Hasta</label>
          <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
            className="block w-full h-9 mt-0.5 px-2 border rounded-lg text-sm" data-testid="purchase-filter-to" />
        </div>
        <div className="min-w-[180px]">
          <label className="text-[10px] font-semibold text-slate-500 uppercase">Proveedor</label>
          <select value={filterSupplier} onChange={e => setFilterSupplier(e.target.value)}
            className="block w-full h-9 mt-0.5 px-2 border rounded-lg text-sm bg-white" data-testid="purchase-filter-supplier">
            <option value="">Todos</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <Button size="sm" className="h-9 text-xs bg-green-600 hover:bg-green-700 text-white gap-1.5"
          onClick={handleExportExcel} disabled={exporting} data-testid="purchase-export-excel">
          <FileXls size={14} weight="bold" /> {exporting ? "Exportando..." : "Exportar Excel"}
        </Button>
      </div>
      <div className="bg-white border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b text-left">
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600">Folio</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600">Fecha</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600">Proveedor</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600">Pago</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-center">Items</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-right">Total</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(o => (
              <tr key={o.id} className="border-b hover:bg-blue-50/30">
                <td className="px-3 py-2 text-xs font-mono font-bold text-blue-700">{o.folio}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{(o.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2 text-xs font-medium text-slate-800">{o.supplier_name}</td>
                <td className="px-3 py-2 text-xs text-slate-500 capitalize">{o.payment_method}</td>
                <td className="px-3 py-2 text-xs text-center">{(o.items || []).length}</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-slate-900">${(o.total || 0).toLocaleString("es-MX")}</td>
                <td className="px-3 py-2 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => setSelectedOrder(o)} title="Ver detalle"
                      className="p-1.5 rounded-md text-blue-500 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      data-testid={`purchase-view-${o.id}`}>
                      <Eye size={15} weight="bold" />
                    </button>
                    <button onClick={() => handleDownloadPdf(o.id, o.folio)} title="Descargar PDF"
                      className="p-1.5 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                      data-testid={`purchase-pdf-${o.id}`}>
                      <FilePdf size={15} weight="bold" />
                    </button>
                    {isAdmin && (
                      <button onClick={() => { if (window.confirm(`¿Eliminar la orden ${o.folio}? Esta accion no se puede deshacer.`)) handleDelete(o.id); }}
                        title="Eliminar" disabled={deleting === o.id}
                        className="p-1.5 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-40"
                        data-testid={`purchase-delete-${o.id}`}>
                        <Trash size={15} weight="bold" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {orders.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Sin compras registradas</p>}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white border rounded-lg px-4 py-2.5">
          <p className="text-xs text-slate-500">Pagina {page + 1} de {totalPages} ({totalOrders} ordenes)</p>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="px-3 py-1.5 text-xs font-semibold rounded-md border bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="purchase-page-prev">Anterior</button>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              className="px-3 py-1.5 text-xs font-semibold rounded-md border bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="purchase-page-next">Siguiente</button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="purchase-detail-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden mx-4 flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b bg-slate-50">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Receipt size={18} weight="duotone" className="text-blue-600" /> Detalle de Compra
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">Folio: <span className="font-mono font-bold text-blue-700">{selectedOrder.folio}</span></p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleDownloadPdf(selectedOrder.id, selectedOrder.folio)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                  data-testid="purchase-detail-pdf-btn">
                  <FilePdf size={14} weight="bold" /> PDF
                </button>
                <button onClick={() => setSelectedOrder(null)} className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors" data-testid="purchase-detail-close">
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto p-5 space-y-4 flex-1">
              {/* Info grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[10px] text-slate-500 uppercase font-semibold">Proveedor</p>
                  <p className="text-sm font-bold text-slate-800 mt-0.5">{selectedOrder.supplier_name}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[10px] text-slate-500 uppercase font-semibold">Fecha</p>
                  <p className="text-sm font-bold text-slate-800 mt-0.5">{(selectedOrder.created_at || "").slice(0, 10)}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[10px] text-slate-500 uppercase font-semibold">Metodo de Pago</p>
                  <p className="text-sm font-bold text-slate-800 mt-0.5 capitalize">{selectedOrder.payment_method}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[10px] text-slate-500 uppercase font-semibold">Referencia</p>
                  <p className="text-sm font-bold text-slate-800 mt-0.5">{selectedOrder.reference || "-"}</p>
                </div>
              </div>

              {/* Items table */}
              <div className="border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-blue-600 text-white">
                      <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase">SKU</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase">Producto</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold uppercase">Cant.</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase">Costo Unit.</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase">Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedOrder.items || []).map((item, idx) => (
                      <tr key={idx} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="px-3 py-2 text-xs font-mono text-slate-600">{item.sku}</td>
                        <td className="px-3 py-2 text-xs font-medium text-slate-800">{item.name}</td>
                        <td className="px-3 py-2 text-xs text-center font-mono">{item.quantity}</td>
                        <td className="px-3 py-2 text-xs text-right font-mono">${(item.unit_cost || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</td>
                        <td className="px-3 py-2 text-xs text-right font-mono font-bold">${(item.line_total || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-1.5">
                  <div className="flex justify-between text-xs text-slate-600"><span>Subtotal:</span><span className="font-mono font-semibold">${(selectedOrder.subtotal || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></div>
                  <div className="flex justify-between text-xs text-slate-600"><span>IVA ({selectedOrder.tax_rate || 16}%):</span><span className="font-mono font-semibold">${(selectedOrder.tax_amount || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></div>
                  <div className="flex justify-between text-sm font-bold text-slate-900 pt-1.5 border-t"><span>Total:</span><span className="font-mono">${(selectedOrder.total || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></div>
                </div>
              </div>

              {/* Notes */}
              {selectedOrder.notes && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-[10px] text-amber-700 uppercase font-semibold mb-1">Notas</p>
                  <p className="text-xs text-slate-700">{selectedOrder.notes}</p>
                </div>
              )}

              {/* Registered by */}
              <p className="text-[10px] text-slate-400 text-right">Registrado por: {selectedOrder.created_by_name || "-"}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =================== SUPPLIERS MANAGER ===================
function SuppliersManager() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", rfc: "", contact_name: "", phone: "", email: "", address: "", bank_name: "", bank_account: "", clabe: "", payment_method: "transferencia", notes: "" });

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/purchases/suppliers`, { headers });
      setSuppliers(res.data || []);
    } catch {} finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      if (editId) {
        await axios.put(`${API}/purchases/suppliers/${editId}`, form, { headers });
        toast.success("Proveedor actualizado");
      } else {
        await axios.post(`${API}/purchases/suppliers`, form, { headers });
        toast.success("Proveedor creado");
      }
      setShowForm(false);
      setEditId(null);
      setForm({ name: "", rfc: "", contact_name: "", phone: "", email: "", address: "", bank_name: "", bank_account: "", clabe: "", payment_method: "transferencia", notes: "" });
      fetchSuppliers();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const startEdit = (s) => {
    setEditId(s.id);
    setForm({ name: s.name || "", rfc: s.rfc || "", contact_name: s.contact_name || "", phone: s.phone || "", email: s.email || "", address: s.address || "", bank_name: s.bank_name || "", bank_account: s.bank_account || "", clabe: s.clabe || "", payment_method: s.payment_method || "transferencia", notes: s.notes || "" });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar proveedor?")) return;
    try { await axios.delete(`${API}/purchases/suppliers/${id}`, { headers }); toast.success("Eliminado"); fetchSuppliers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="flex justify-center py-10"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-4" data-testid="suppliers-manager">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => { setShowForm(true); setEditId(null); setForm({ name: "", rfc: "", contact_name: "", phone: "", email: "", address: "", bank_name: "", bank_account: "", clabe: "", payment_method: "transferencia", notes: "" }); }} data-testid="add-supplier-btn">
          <Plus size={14} className="mr-1" /> Nuevo Proveedor
        </Button>
      </div>

      {showForm && (
        <div className="bg-white border rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-bold text-slate-800">{editId ? "Editar" : "Nuevo"} Proveedor</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className="text-[10px] font-semibold text-slate-500">Nombre *</label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="h-9 text-sm mt-0.5" data-testid="supplier-name" /></div>
            <div><label className="text-[10px] font-semibold text-slate-500">RFC</label><Input value={form.rfc} onChange={e => setForm({ ...form, rfc: e.target.value })} className="h-9 text-sm mt-0.5" /></div>
            <div><label className="text-[10px] font-semibold text-slate-500">Contacto</label><Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} className="h-9 text-sm mt-0.5" /></div>
            <div><label className="text-[10px] font-semibold text-slate-500">Telefono</label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="h-9 text-sm mt-0.5" /></div>
            <div><label className="text-[10px] font-semibold text-slate-500">Email</label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="h-9 text-sm mt-0.5" /></div>
            <div><label className="text-[10px] font-semibold text-slate-500">Direccion</label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="h-9 text-sm mt-0.5" /></div>
            <div><label className="text-[10px] font-semibold text-slate-500">Banco</label><Input value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} className="h-9 text-sm mt-0.5" /></div>
            <div><label className="text-[10px] font-semibold text-slate-500">No. Cuenta</label><Input value={form.bank_account} onChange={e => setForm({ ...form, bank_account: e.target.value })} className="h-9 text-sm mt-0.5" /></div>
            <div><label className="text-[10px] font-semibold text-slate-500">CLABE</label><Input value={form.clabe} onChange={e => setForm({ ...form, clabe: e.target.value })} className="h-9 text-sm mt-0.5" /></div>
            <div><label className="text-[10px] font-semibold text-slate-500">Metodo de Pago</label>
              <select value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })} className="w-full h-9 mt-0.5 px-2 border rounded-lg text-sm">
                <option value="transferencia">Transferencia</option><option value="efectivo">Efectivo</option><option value="cheque">Cheque</option><option value="credito">Credito</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} data-testid="supplier-save">{editId ? "Actualizar" : "Guardar"}</Button>
            <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditId(null); }}>Cancelar</Button>
          </div>
        </div>
      )}

      <div className="bg-white border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b text-left">
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600">Nombre</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600">RFC</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600">Contacto</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600">Telefono</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600">Pago</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map(s => (
              <tr key={s.id} className="border-b hover:bg-blue-50/30">
                <td className="px-3 py-2 text-xs font-bold text-slate-800">{s.name}</td>
                <td className="px-3 py-2 text-xs text-slate-600 font-mono">{s.rfc || "-"}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{s.contact_name || "-"}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{s.phone || "-"}</td>
                <td className="px-3 py-2 text-xs text-slate-500 capitalize">{s.payment_method}</td>
                <td className="px-3 py-2 text-center">
                  <button onClick={() => startEdit(s)} className="text-blue-500 hover:text-blue-700 text-xs font-medium mr-2">Editar</button>
                  <button onClick={() => handleDelete(s.id)} className="text-red-400 hover:text-red-600"><Trash size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {suppliers.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Sin proveedores</p>}
      </div>
    </div>
  );
}
